import { useMemo } from 'react'
import Chart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import type { ChartCard } from '@/features/dashboard/api/schemas'
import { formatAbbrev } from '@/features/dashboard/format/format'
import { baseOptions } from './lib/chartTheme'
import { chartSummary } from './lib/chartSummary'
import { useChartPalette } from './lib/useChartPalette'

type BarChartProps = {
  card: ChartCard
  height?: number
}

/**
 * Deep-dive chart, mirroring bristol's `renderDeepDiveChart` for bar/line
 * charts: rounded columns with the value printed above each one, warm grid
 * lines, abbreviated axis labels and a legend underneath.
 */
export const BarChart = ({ card, height = 340 }: BarChartProps) => {
  const { theme, palette } = useChartPalette()
  const isBar = card.chartType === 'bar'

  const options = useMemo((): ApexOptions => {
    const base = baseOptions(palette)
    const abbrev = (value: number | null) =>
      value === null ? '' : formatAbbrev(value, card.unit)
    return {
      ...base,
      chart: {
        ...base.chart,
        type: isBar ? 'bar' : 'line',
        animations: { speed: 300 },
      },
      colors: palette.series,
      theme: { mode: theme },
      grid: {
        borderColor: palette.grid,
        padding: isBar ? { top: 20 } : {},
      },
      dataLabels: isBar
        ? {
            enabled: true,
            offsetY: -14,
            style: {
              fontSize: '11px',
              fontWeight: 600,
              colors: [palette.text],
            },
            background: { enabled: false },
            formatter: value =>
              abbrev(typeof value === 'number' ? value : null),
          }
        : (base.dataLabels ?? {}),
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '55%',
          dataLabels: { position: 'top' },
        },
      },
      stroke: isBar ? { show: false } : { curve: 'monotoneCubic', width: 2.5 },
      markers: isBar ? {} : { size: 0, hover: { size: 6 } },
      xaxis: {
        categories: card.categories,
        labels: { style: { colors: palette.label } },
        axisBorder: { color: palette.grid },
        axisTicks: { color: palette.grid },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          style: { colors: palette.label },
          formatter: value => abbrev(value),
        },
      },
      tooltip: { theme, y: { formatter: value => abbrev(value) } },
    }
  }, [card.categories, card.unit, isBar, palette, theme])

  return (
    <div role='img' aria-label={chartSummary(card)}>
      <Chart
        type={isBar ? 'bar' : 'line'}
        height={height}
        width='100%'
        options={options}
        series={card.series}
      />
    </div>
  )
}
